'use client'

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react'

import { Check, Lock, Unlock } from 'lucide-react'

import { useAvatarCatalog } from '@/features/profiles/hooks/useAvatarCatalog'
import { cosmeticCriteriaLines } from '@/features/profiles/lib/cosmeticCriteriaLines'
import { getCosmeticUrlsForIds } from '@/features/profiles/lib/cosmetic-preview-urls'
import type {
  CosmeticProfileEntry,
  CosmeticProfileKind,
  CosmeticProfileStatus
} from '@/features/profiles/types/avatar-catalog'
import { cn } from '@/shared/lib/utils'
import { api } from '@/shared/services/api'
import { AdminAutoCompleteButton } from '@/shared/ui/AdminAutoCompleteButton'
import { Button } from '@/shared/ui/Button'
import { Dialog, DialogContent, DialogTitle } from '@/shared/ui/dialog'
import { Tabs, Tab } from '@/shared/ui/Tabs'

import { CosmeticPreviewStack } from './CosmeticPreviewStack'
import { CosmeticRedeemModalScreen } from './CosmeticRedeemModalScreen'

const KIND_ORDER: CosmeticProfileKind[] = ['avatar', 'frame', 'background']

const KIND_LABELS: Record<CosmeticProfileKind, string> = {
  avatar: 'Avatar',
  frame: 'Borda',
  background: 'Plano de fundo'
}

function statusAriaLabel(entry: {
  status: CosmeticProfileStatus
  unlock_eligible: boolean
}): string {
  if (entry.status === 'unlocked') return 'Na sua coleção'
  if (entry.unlock_eligible) return 'Bloqueado — pode desbloquear'
  return 'Bloqueado'
}

function entryThumbUrl(entry: CosmeticProfileEntry): string {
  return entry.image?.file?.url ?? '/placeholders/avatar.png'
}

function CardStatusGlyph({ status }: { status: CosmeticProfileStatus }) {
  const shell =
    'flex size-6 items-center justify-center rounded-full bg-white/85 text-gray-500 shadow-sm ring-1 ring-black/5 backdrop-blur-[2px]'
  if (status === 'unlocked') {
    return (
      <span
        className={cn(shell, 'text-emerald-700/55')}
        title="Na coleção"
        aria-hidden
      >
        <Unlock className="size-3.5" strokeWidth={2} />
      </span>
    )
  }
  return (
    <span
      className={cn(shell, 'text-gray-400/90')}
      title="Bloqueado"
      aria-hidden
    >
      <Lock className="size-3.5" strokeWidth={2} />
    </span>
  )
}

export interface AvatarCatalogPanelProps {
  onAvatarChanged?: () => void | Promise<void>
  onDirtyChange?: (dirty: boolean) => void
}

export type AvatarCatalogPanelHandle = {
  saveSelections: () => Promise<void>
  hasDirtySelection: () => boolean
}

export const AvatarCatalogPanel = forwardRef<
  AvatarCatalogPanelHandle,
  AvatarCatalogPanelProps
>(function AvatarCatalogPanel({ onAvatarChanged, onDirtyChange }, ref) {
  const { data, isLoading, error, refetchCatalog, invalidateCatalog } =
    useAvatarCatalog(true)
  const [kindTab, setKindTab] = useState<CosmeticProfileKind>('avatar')
  const [acquiredOnly, setAcquiredOnly] = useState(false)
  const [redeemEntry, setRedeemEntry] = useState<CosmeticProfileEntry | null>(
    null
  )
  const [previewDelta, setPreviewDelta] = useState<
    Partial<Record<CosmeticProfileKind, number | null>>
  >({})
  const [globalMessage, setGlobalMessage] = useState<{
    kind: 'success' | 'error'
    text: string
  } | null>(null)

  const list = data?.cosmetics ?? []

  const serverActive = useMemo(
    () => ({
      avatar: data?.active_avatar_id ?? null,
      frame: data?.active_frame_id ?? null,
      background: data?.active_background_id ?? null
    }),
    [data?.active_avatar_id, data?.active_frame_id, data?.active_background_id]
  )

  const idForKind = useCallback(
    (k: CosmeticProfileKind) =>
      previewDelta[k] !== undefined ? previewDelta[k]! : serverActive[k],
    [previewDelta, serverActive]
  )

  const hasDirtySelection = useCallback(() => {
    return KIND_ORDER.some(k => {
      const want = idForKind(k)
      const srv = serverActive[k]
      return want != null && want !== srv
    })
  }, [idForKind, serverActive])

  useEffect(() => {
    onDirtyChange?.(hasDirtySelection())
  }, [hasDirtySelection, onDirtyChange])

  useImperativeHandle(
    ref,
    () => ({
      hasDirtySelection,
      saveSelections: async () => {
        if (!data) return

        setGlobalMessage(null)

        const pendingKinds = KIND_ORDER.filter(k => {
          const want = idForKind(k)
          return want != null && want !== serverActive[k]
        })

        if (pendingKinds.length === 0) {
          setGlobalMessage({
            kind: 'success',
            text: 'Nada novo para salvar.'
          })
          return
        }

        const saveableIds: number[] = []
        const blockedKinds: CosmeticProfileKind[] = []

        for (const k of pendingKinds) {
          const id = idForKind(k)!
          const entry = list.find(
            c => c.id === id && (c.kind as CosmeticProfileKind) === k
          )
          if (entry?.status === 'unlocked') {
            saveableIds.push(id)
          } else {
            blockedKinds.push(k)
          }
        }

        if (saveableIds.length === 0) {
          setGlobalMessage({
            kind: 'error',
            text: 'Itens selecionados ainda não estão na sua coleção. Desbloqueie antes de salvar.'
          })
          return
        }

        try {
          for (const id of saveableIds) {
            await api.put(`/user/cosmetics/${id}/select`)
          }
          await refetchCatalog()
          setPreviewDelta({})
          invalidateCatalog()
          await onAvatarChanged?.()
          if (blockedKinds.length > 0) {
            setGlobalMessage({
              kind: 'success',
              text: 'Aparência salva. Alguns itens na pré-visualização ainda estão bloqueados e não foram aplicados.'
            })
          } else {
            setGlobalMessage({
              kind: 'success',
              text: 'Aparência salva.'
            })
          }
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } }
          const msg =
            err.response?.data?.error ??
            'Não foi possível salvar. Tente novamente.'
          setGlobalMessage({ kind: 'error', text: msg })
          await refetchCatalog()
        }
      }
    }),
    [
      data,
      hasDirtySelection,
      idForKind,
      invalidateCatalog,
      list,
      onAvatarChanged,
      refetchCatalog,
      serverActive
    ]
  )

  const previewUrls = useMemo(
    () =>
      getCosmeticUrlsForIds(list, {
        avatarId: idForKind('avatar'),
        frameId: idForKind('frame'),
        backgroundId: idForKind('background')
      }),
    [list, idForKind]
  )

  const kindsInCatalog = useMemo(() => {
    const s = new Set<string>()
    for (const c of list) {
      if (KIND_ORDER.includes(c.kind as CosmeticProfileKind)) {
        s.add(c.kind)
      }
    }
    return KIND_ORDER.filter(k => s.has(k))
  }, [list])

  useEffect(() => {
    if (kindsInCatalog.length === 0) return
    if (!kindsInCatalog.includes(kindTab)) {
      setKindTab(kindsInCatalog[0]!)
    }
  }, [kindsInCatalog, kindTab])

  const filtered = useMemo(() => {
    return list.filter(c => {
      if (c.kind !== kindTab) return false
      if (acquiredOnly && c.status !== 'unlocked') return false
      return true
    })
  }, [list, kindTab, acquiredOnly])

  const beginRedeem = useCallback((entry: CosmeticProfileEntry) => {
    setRedeemEntry(entry)
  }, [])

  const onPickEntry = (entry: CosmeticProfileEntry) => {
    const k = entry.kind as CosmeticProfileKind
    if (!KIND_ORDER.includes(k)) return
    setPreviewDelta(prev => ({ ...prev, [k]: entry.id }))
  }

  if (isLoading && !data) {
    return (
      <p className="text-sm text-gray-500 py-2" aria-live="polite">
        Carregando catálogo…
      </p>
    )
  }

  if (error) {
    return (
      <p className="text-sm text-red-600 py-2">
        Não foi possível carregar o catálogo de aparência.
      </p>
    )
  }

  return (
    <>
    <div className="flex flex-col gap-2 min-h-0 flex-1">
      <div className="shrink-0 flex flex-col items-center gap-1">
        <CosmeticPreviewStack
          compact
          backgroundUrl={previewUrls.backgroundUrl}
          avatarUrl={previewUrls.avatarUrl}
          frameUrl={previewUrls.frameUrl}
          nameHint={KIND_LABELS[kindTab]}
        />
        <span className="sr-only">Pré-visualização da combinação atual</span>
      </div>

      <div className="shrink-0 flex flex-col gap-1.5">
        <Tabs
          value={kindTab}
          onValueChange={v => setKindTab(v as CosmeticProfileKind)}
          className="justify-center flex-wrap gap-1"
          visibleCountMobile={3}
        >
          {kindsInCatalog.map(k => (
            <Tab key={k} value={k}>
              {KIND_LABELS[k]}
            </Tab>
          ))}
        </Tabs>
        <button
          type="button"
          role="switch"
          aria-checked={acquiredOnly}
          onClick={() => setAcquiredOnly(v => !v)}
          className="flex items-center justify-center gap-2 mx-auto text-[11px] text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jacaranda/25 rounded-full py-0.5 px-2"
        >
          <span
            className={cn(
              'w-7 h-3.5 rounded-full border border-gray-200/90 bg-gray-100/80 relative transition-colors',
              acquiredOnly && 'bg-jacaranda/15 border-jacaranda/30'
            )}
            aria-hidden
          >
            <span
              className={cn(
                'absolute top-px left-px size-2.5 rounded-full bg-white shadow-sm transition-transform',
                acquiredOnly && 'translate-x-3.5 bg-jacaranda/90'
              )}
            />
          </span>
          <span>Só adquiridos</span>
        </button>
      </div>

      {globalMessage && (
        <div
          className={cn(
            'rounded-md px-2.5 py-1.5 text-xs shrink-0',
            globalMessage.kind === 'success'
              ? 'bg-emerald-50/90 text-emerald-900/90 border border-emerald-200/60'
              : 'bg-red-50/90 text-red-900/90 border border-red-200/60'
          )}
          role="status"
        >
          {globalMessage.text}
        </div>
      )}

      <div
        className={cn(
          'flex-1 overflow-y-auto overflow-x-hidden overscroll-contain pr-0.5 -mr-0.5',
          'min-h-[280px] max-h-[min(58dvh,520px)]',
          'sm:min-h-[300px] sm:max-h-[min(56dvh,540px)]'
        )}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-2.5 pb-2">
          {filtered.map(entry => {
            const lines = cosmeticCriteriaLines(entry.criteria)
            const img = entryThumbUrl(entry)
            const isPreviewed =
              idForKind(entry.kind as CosmeticProfileKind) === entry.id
            const lockedVisually = entry.status !== 'unlocked'
            const redeemGate =
              entry.status !== 'unlocked' && entry.unlock_eligible
            const showPickHighlight =
              !redeemGate && (entry.active || isPreviewed)

            const activateCard = () => {
              if (redeemGate) beginRedeem(entry)
              else onPickEntry(entry)
            }

            return (
              <div
                key={entry.id}
                role="button"
                tabIndex={0}
                aria-label={
                  redeemGate
                    ? `${entry.name}. ${statusAriaLabel(entry)} Abre o resgate.`
                    : `${entry.name}. ${statusAriaLabel(entry)}.`
                }
                onClick={activateCard}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    activateCard()
                  }
                }}
                className={cn(
                  'relative rounded-lg border p-2 flex flex-col gap-1 min-w-0 text-left transition-colors cursor-pointer',
                  'hover:border-gray-300/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-jacaranda/30',
                  showPickHighlight
                    ? 'border-jacaranda/50 ring-1 ring-jacaranda/25 bg-jacaranda/4'
                    : 'border-gray-200/70 bg-white/40'
                )}
              >
                {redeemGate ? (
                  <div
                    className="pointer-events-none absolute inset-0 z-[1] rounded-lg bg-black/12"
                    aria-hidden
                  />
                ) : null}
                <div
                  className={cn(
                    'relative flex flex-col gap-1 min-w-0',
                    redeemGate && 'z-[2]'
                  )}
                >
                  <div className="relative mx-auto w-fit">
                    <img
                      src={img}
                      alt=""
                      width={80}
                      height={80}
                      className={cn(
                        'object-cover size-20 transition-opacity rounded-xl',
                        lockedVisually && 'opacity-[0.72]'
                      )}
                    />
                    {redeemGate ? (
                      <div
                        className="pointer-events-none absolute inset-0 flex flex-col justify-end rounded-xl p-1"
                      >
                        <Button
                          as="div"
                          variant="reward"
                          size="sm"
                          className="w-full pointer-events-none select-none"
                          aria-hidden
                        >
                          Resgatar
                        </Button>
                      </div>
                    ) : null}
                    <div className="pointer-events-none absolute top-0.5 right-0.5 z-10">
                      <CardStatusGlyph status={entry.status} />
                    </div>
                    {entry.active && (
                      <span
                        className="pointer-events-none absolute bottom-0.5 left-0.5 z-10 flex size-5 items-center justify-center rounded-full bg-jacaranda text-white shadow-sm ring-1 ring-white/50"
                        title="Em uso"
                        aria-hidden
                      >
                        <Check className="size-3" strokeWidth={2.5} />
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-medium text-center text-gray-700/90 line-clamp-2 leading-snug px-0.5">
                    {entry.name}
                  </p>
                  {lines.length > 0 && (
                    <p className="text-[10px] leading-tight text-gray-400/90 text-center line-clamp-2 px-0.5">
                      {lines.slice(0, 2).join(' · ')}
                    </p>
                  )}

                  {/* Cosmético não se "conclui": ele é concedido quando a regra
                      fecha. O botão satisfaz o progresso que a regra exige e
                      deixa o UnlockerService conceder — e, quando a regra é de
                      identidade ou tempo, informa que não há como satisfazê-la. */}
                  {lockedVisually ? (
                    <AdminAutoCompleteButton
                      type="Cosmetic"
                      id={entry.id}
                      label="Auto-liberar"
                      className="mx-auto"
                    />
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
    <Dialog
      open={redeemEntry !== null}
      onOpenChange={open => {
        if (!open) setRedeemEntry(null)
      }}
    >
      <DialogContent
        hideCloseButton
        className={cn(
          'z-[100] w-[min(92%,400px)] max-w-[min(100%,400px)] border-0 bg-transparent p-0 shadow-none',
          'gap-0 overflow-visible sm:max-w-lg'
        )}
      >
        <DialogTitle className="sr-only">
          {redeemEntry
            ? `Resgatar ${redeemEntry.name}`
            : 'Resgatar cosmético'}
        </DialogTitle>
        {redeemEntry ? (
          <CosmeticRedeemModalScreen
            entry={redeemEntry}
            onHide={() => setRedeemEntry(null)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
    </>
  )
})

AvatarCatalogPanel.displayName = 'AvatarCatalogPanel'
